<template>
  <div class="condition-builder">
    <div class="condition-builder__group">
      <div class="condition-builder__group-header">
        <div class="condition-builder__group-type">
          <select
            v-if="isEdit"
            v-model="localConditions.type"
            class="condition-builder__type-select"
            @change="emitUpdate"
          >
            <option value="AND">AND (All must match)</option>
            <option value="OR">OR (Any can match)</option>
          </select>
          <span v-else class="condition-builder__type-badge">{{ localConditions.type }}</span>
        </div>
      </div>

      <div v-if="localConditions.rules && localConditions.rules.length > 0" class="condition-builder__rules">
        <div
          v-for="(rule, index) in localConditions.rules"
          :key="index"
          class="condition-builder__rule"
        >
          <!-- Nested condition group -->
          <template v-if="rule.type === 'AND' || rule.type === 'OR'">
            <div class="condition-builder__nested-group">
              <ConditionBuilder
                :conditions="rule"
                :isEdit="isEdit"
                :fieldOptions="fieldOptions"
                @update="updateNestedGroup(index, $event)"
              />
              <button
                v-if="isEdit"
                class="condition-builder__remove-btn"
                @click="removeRule(index)"
              >
                <i class="fa fa-times"></i>
              </button>
            </div>
          </template>

          <!-- Simple condition -->
          <template v-else>
            <div class="condition-builder__condition-row">
              <Input
                :inputData="fieldOptionInput"
                :model="rule.field"
                :isEdit="isEdit"
                @updateModel="updateRuleField(rule, $event)"
                customStyle="min-width: 150px; flex: 1;"
              />

              <Input
                :inputData="getOperatorInputForRule(rule)"
                :model="rule.operator"
                :isEdit="isEdit"
                @updateModel="updateRuleOperator(rule, $event)"
                customStyle="width: 200px;"
              />

              <Input
                :key="`value-input-${rule.field}-${rule.operator}-${index}`"
                :inputData="getValueInputForRule(rule)"
                :model="rule.value"
                :isEdit="isEdit"
                @updateModel="updateRuleValue(rule, $event)"
                customStyle="min-width: 150px; flex: 1;"
              />

              <button
                v-if="isEdit"
                class="condition-builder__remove-btn"
                @click="removeRule(index)"
              >
                <i class="fa fa-times"></i>
              </button>
            </div>
          </template>
        </div>
      </div>

      <div v-else class="condition-builder__empty">
        <span v-if="!isEdit">No conditions defined - applies to all items</span>
        <span v-else>Click "Add Condition" to create a condition</span>
      </div>

      <div v-if="isEdit" class="condition-builder__actions">
        <button
          v-if="isEdit"
          class="condition-builder__add-btn"
          @click="addCondition"
        >
          <i class="fa fa-plus"></i> Add Condition
        </button>
        <button class="condition-builder__add-group-btn" @click="addGroup('AND')">
          <i class="fa fa-layer-group"></i> Add AND Group
        </button>
        <button class="condition-builder__add-group-btn" @click="addGroup('OR')">
          <i class="fa fa-layer-group"></i> Add OR Group
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { conditionOperatorOptions, conditionFieldOptions, conditionSearchInputs } from '../inputs';
import Input from '@/components/Input/Input.vue';

// Map condition fields to their corresponding search input keys
const FIELD_TO_SEARCH_MAP = {
  'product.group': 'group',
  'product.supplier': 'supplier',
  'product.brand': 'brand',
  'product._id': 'product',
};

// Fields that should use boolean Yes/No select instead of text input
const BOOLEAN_FIELDS = ['customer.isVeteran', 'customer.firstPurchase'];

// Operators that make sense for boolean fields
const BOOLEAN_OPERATORS = [
  { label: "Equals", code: "eq" },
  { label: "Not Equals", code: "neq" },
];

export default {
  name: "ConditionBuilder",
  components: {
    Input,
  },
  props: {
    conditions: {
      type: Object,
      default: () => ({ type: "AND", rules: [] }),
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    fieldOptions: {
      type: Array,
      default: null, // If null, uses default conditionFieldOptions
    },
  },
  data() {
    return {
      localConditions: JSON.parse(JSON.stringify(this.conditions)),
    };
  },
  watch: {
    conditions: {
      handler(newVal) {
        this.localConditions = JSON.parse(JSON.stringify(newVal));
      },
      deep: true,
    },
  },

  computed: {
    activeFieldOptions() {
      return this.fieldOptions || conditionFieldOptions;
    },
    fieldOptionInput() {
      return {
        id: "field",
        type: "select",
        placeholder: "Select Field",
        options: this.activeFieldOptions
      }
    },
    operatorOptionInput() {
      return {
        id: "operator",
        type: "select",
        placeholder: "Select Operator",
        options: conditionOperatorOptions
      }
    },
  },
  methods: {
    emitUpdate() {
      this.$emit("update", this.localConditions);
    },

    /**
     * Get the appropriate input configuration for a rule's value field.
     * Returns a search input for entity-based fields, boolean select for boolean fields,
     * or a plain text input otherwise.
     * Uses search-multi type for 'in' and 'nin' operators to allow multiple selections.
     */
    getValueInputForRule(rule) {
      const searchKey = FIELD_TO_SEARCH_MAP[rule.field];
      const ruleIndex = this.localConditions.rules.indexOf(rule);
      const isMultiSelect = ['in', 'nin'].includes(rule.operator);

      if (searchKey && conditionSearchInputs[searchKey]) {
        const baseConfig = conditionSearchInputs[searchKey];
        
        // For multi-select, add checkbox column to enable row selection
        let columnHeaders = baseConfig.columnHeaders;
        if (isMultiSelect) {
          const checkboxColumn = {
            id: "checked",
            type: "checkbox",
            action: "selectItem",
            size: "small",
            title: "Select",
          };
          // Prepend checkbox column if not already present
          if (!columnHeaders.some(col => col.id === "checked")) {
            columnHeaders = [checkboxColumn, ...columnHeaders];
          }
        }
        
        return {
          ...baseConfig,
          columnHeaders,
          id: `value-${rule.field}-${ruleIndex}`,
          nestedId: String(ruleIndex),  // Pass rule index as string (SearchModal expects String type)
          title: '',                     // Hide the label for condition builder context
          type: isMultiSelect ? 'search-multi' : 'search',  // Use multi-select for in/nin operators
        };
      }

      // Boolean fields get a Yes/No select dropdown
      if (BOOLEAN_FIELDS.includes(rule.field)) {
        return {
          id: "value",
          type: "select",
          placeholder: "Select Value",
          options: [
            { label: "Yes", code: true },
            { label: "No", code: false },
          ],
        };
      }

      // Default text input for non-entity fields
      return {
        id: "value",
        type: "input",
        placeholder: "Enter Value",
      };
    },

    /**
     * Get the appropriate operator input configuration for a rule.
     * Boolean fields only support eq/neq operators.
     */
    getOperatorInputForRule(rule) {
      const options = BOOLEAN_FIELDS.includes(rule.field) 
        ? BOOLEAN_OPERATORS 
        : conditionOperatorOptions;
      
      return {
        id: "operator",
        type: "select",
        placeholder: "Select Operator",
        options
      };
    },

    /**
     * Check if a field uses entity search (for display formatting)
     */
    isEntityField(field) {
      return !!FIELD_TO_SEARCH_MAP[field];
    },

    /**
     * Check if a field is a boolean field (Yes/No select)
     */
    isBooleanField(field) {
      return BOOLEAN_FIELDS.includes(field);
    },

    /**
     * Get the display value for a rule's value field.
     * For entity fields, shows the name; for others, shows the raw value.
     */
    getDisplayValue(rule) {
      if (this.isEntityField(rule.field) && typeof rule.value === 'object' && rule.value) {
        return rule.value.name || rule.value._id || '-';
      }
      return rule.value || '-';
    },

    /**
     * Called by the Input component when an item is selected from search modal.
     * Uses nestedId (rule index as string) to identify which rule to update.
     */
    selectItemFromSearch(entity, item, nestedId) {
      const ruleIndex = parseInt(nestedId, 10);
      const rule = this.localConditions.rules[ruleIndex];

      if (rule) {
        // Store both _id and name so we can display the name but use the _id for evaluation
        rule.value = {
          _id: item._id,
          name: item.name,
        };
        this.emitUpdate();
      }
    },

    /**
     * Called by the Input component when clearing a search selection.
     * Uses nestedId (rule index as string) to identify which rule to clear.
     */
    clearItemFromSearch(entity, nestedId) {
      const ruleIndex = parseInt(nestedId, 10);
      const rule = this.localConditions.rules[ruleIndex];

      if (rule) {
        rule.value = null;
        this.emitUpdate();
      }
    },

    addCondition() {
      if (!this.localConditions.rules) {
        this.localConditions.rules = [];
      }
      this.localConditions.rules.push({
        field: "",
        operator: "eq",
        value: "",
      });
      this.emitUpdate();
    },

    addGroup(type) {
      if (!this.localConditions.rules) {
        this.localConditions.rules = [];
      }
      this.localConditions.rules.push({
        type: type,
        rules: [],
      });
      this.emitUpdate();
    },

    updateRuleField(rule, value) {
      const wasEntityField = this.isEntityField(rule.field);
      const isEntityField = this.isEntityField(value);
      const wasBooleanField = this.isBooleanField(rule.field);
      const isBooleanField = this.isBooleanField(value);

      rule.field = value;

      // Clear/reset the value when switching between field types
      if (wasEntityField !== isEntityField || wasBooleanField !== isBooleanField) {
        if (isEntityField) {
          rule.value = null;
        } else if (isBooleanField) {
          rule.value = true;
          // Reset operator to 'eq' for boolean fields if current operator is invalid
          if (!['eq', 'neq'].includes(rule.operator)) {
            rule.operator = 'eq';
          }
        } else {
          rule.value = "";
        }
      }

      this.emitUpdate();
    },

    updateRuleOperator(rule, value) {
      const wasMultiSelect = ['in', 'nin'].includes(rule.operator);
      const isMultiSelect = ['in', 'nin'].includes(value);
      
      rule.operator = value;
      
      // Handle value conversion when switching between single and multi-select for entity fields
      if (this.isEntityField(rule.field) && wasMultiSelect !== isMultiSelect) {
        if (isMultiSelect) {
          // Converting from single to multi: wrap value in array if it exists
          rule.value = rule.value ? [rule.value] : [];
        } else {
          // Converting from multi to single: take first item or null
          rule.value = Array.isArray(rule.value) && rule.value.length > 0 ? rule.value[0] : null;
        }
      }
      
      this.emitUpdate();
    },

    updateRuleValue(rule, value) {
      // For search inputs, value will be the selected object { _id, name, ... }
      // For regular inputs, value will be a string/number
      rule.value = value;
      this.emitUpdate();
    },

    removeRule(index) {
      this.localConditions.rules.splice(index, 1);
      this.emitUpdate();
    },

    updateNestedGroup(index, newGroup) {
      this.localConditions.rules[index] = newGroup;
      this.emitUpdate();
    },

    getOperatorSymbol(op) {
      const symbols = {
        eq: "=",
        ne: "≠",
        gt: ">",
        gte: "≥",
        lt: "<",
        lte: "≤",
        in: "in",
        nin: "not in",
      };
      return symbols[op] || op;
    },
  },
};
</script>

<style lang="scss" scoped>
.condition-builder {
  &__group {
    padding: 1rem;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 8px;
  }

  &__group-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  &__type-select {
    background: rgba(96, 165, 250, 0.15);
    border: 1px solid rgba(96, 165, 250, 0.3);
    color: #60a5fa;
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
  }

  &__type-badge {
    background: rgba(96, 165, 250, 0.15);
    color: #60a5fa;
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
  }

  &__add-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 6px 12px;
    font-size: 12px;
    background: rgba(96, 165, 250, 0.1);
    border: 1px dashed rgba(96, 165, 250, 0.4);
    color: #60a5fa;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: rgba(96, 165, 250, 0.2);
    }
  }

  &__rules {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__rule {
    position: relative;
  }

  &__condition-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem;
    background: rgba(0, 0, 0, 0.15);
    border-radius: 6px;
    flex-wrap: wrap;
  }

  &__field-select,
  &__operator-select,
  &__value-input {
    padding: 8px 12px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 4px;
    color: inherit;
    font-size: 13px;

    &:focus {
      outline: none;
      border-color: #60a5fa;
    }
  }

  &__field-select {
    flex: 1;
    min-width: 150px;
  }

  &__operator-select {
    width: 80px;
  }

  &__value-input {
    flex: 1;
    min-width: 120px;
  }

  &__field-display,
  &__operator-display,
  &__value-display {
    padding: 8px 12px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
    font-size: 13px;
  }

  &__remove-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: rgba(239, 68, 68, 0.15);
    border: none;
    border-radius: 4px;
    color: #ef4444;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: rgba(239, 68, 68, 0.3);
    }
  }

  &__nested-group {
    position: relative;
    padding-left: 1rem;
    border-left: 2px solid rgba(96, 165, 250, 0.3);

    .condition-builder__remove-btn {
      position: absolute;
      top: 0;
      right: 0;
    }
  }

  &__empty {
    text-align: center;
    padding: 2rem;
    color: rgba(255, 255, 255, 0.5);
    font-size: 13px;
  }

  &__actions {
    display: flex;
    gap: 0.5rem;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px dashed rgba(255, 255, 255, 0.1);
  }

  &__add-group-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 6px 12px;
    font-size: 11px;
    background: transparent;
    border: 1px dashed rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.6);
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: rgba(255, 255, 255, 0.05);
      border-color: rgba(255, 255, 255, 0.4);
    }
  }
}

// Light theme overrides
.light .condition-builder {
  &__group {
    background: rgba(0, 0, 0, 0.03);
  }

  &__condition-row {
    background: rgba(0, 0, 0, 0.04);
  }

  &__field-select,
  &__operator-select,
  &__value-input {
    background: white;
    border-color: rgba(0, 0, 0, 0.15);
    color: #2c3e50;
  }

  &__field-display,
  &__operator-display,
  &__value-display {
    background: rgba(0, 0, 0, 0.04);
    color: #2c3e50;
  }

  &__empty {
    color: #7f8c8d;
  }

  &__actions {
    border-top-color: rgba(0, 0, 0, 0.1);
  }

  &__add-group-btn {
    border-color: rgba(0, 0, 0, 0.2);
    color: #7f8c8d;

    &:hover {
      background: rgba(0, 0, 0, 0.05);
      border-color: rgba(0, 0, 0, 0.4);
    }
  }

  &__nested-group {
    border-left-color: rgba(96, 165, 250, 0.5);
  }
}
</style>

